/**
 * 学籍状态操作区 + 时间线 — spec-school-student-admin.md §5.3 (P0-a).
 *
 * Action buttons are DERIVED from the backend's transition table
 * (GET /students/lifecycle/transitions) — there is no second copy of the
 * state machine here. Illegal actions are not rendered at all (not
 * greyed). The confirmation dialog always shows the impact preview
 * (effects_zh) and collects exactly the fields the table requires.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  useChangeStudentStatus,
  useLifecycleTransitions,
  useReinstateStudent,
  useStudentTimeline,
  type LifecycleRule,
} from "../../api/school";

export const STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  candidate: { label: "待面试", cls: "bg-slate-100 text-slate-600" },
  applicant: { label: "待面试", cls: "bg-slate-100 text-slate-600" },
  interviewed: { label: "已面试", cls: "bg-sky-100 text-sky-700" },
  accepted: { label: "已录取", cls: "bg-violet-100 text-violet-700" },
  active: { label: "在读", cls: "bg-emerald-100 text-emerald-700" },
  on_leave: { label: "休学", cls: "bg-amber-100 text-amber-700" },
  graduated: { label: "毕业", cls: "bg-indigo-100 text-indigo-700" },
  transferred_out: { label: "已转出", cls: "bg-slate-200 text-slate-600" },
  withdrawn: { label: "退学", cls: "bg-rose-100 text-rose-700" },
  rejected: { label: "未录取", cls: "bg-slate-200 text-slate-500" },
};

const FIELD_LABEL: Record<string, string> = {
  reason: "原因",
  effective_date: "生效日期",
  expected_return_date: "预计返校日期",
  transferred_to: "去向学校",
};

export function LifecyclePanel({
  studentId,
  status,
  canManage,
}: {
  studentId: string;
  status: string;
  canManage: boolean;
}) {
  const { t } = useTranslation();
  const transitions = useLifecycleTransitions();
  const change = useChangeStudentStatus(studentId);
  const reinstate = useReinstateStudent(studentId);
  const timeline = useStudentTimeline(studentId);

  const [dialog, setDialog] = useState<{ to: string; rule: LifecycleRule } | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [err, setErr] = useState<string | null>(null);

  const outs = transitions.data?.legal?.[status] ?? {};
  const actions = Object.entries(outs).filter(([, r]) => !r.admissions_only);

  function open(to: string, rule: LifecycleRule) {
    setFields({});
    setErr(null);
    setDialog({ to, rule });
  }

  async function submit() {
    if (!dialog) return;
    setErr(null);
    const required = dialog.rule.required ?? [];
    const missing = required.filter((f) => !fields[f]?.trim());
    if (missing.length) {
      setErr(t("lifecycle.missingFields", { fields: missing.map((f) => FIELD_LABEL[f] ?? f).join("、") }));
      return;
    }
    try {
      if (dialog.to === "active") {
        await reinstate.mutateAsync({ reason: fields.reason });
      } else {
        await change.mutateAsync({
          to_status: dialog.to,
          reason: fields.reason,
          effective_date: fields.effective_date || undefined,
          expected_return_date: fields.expected_return_date || undefined,
          transferred_to: fields.transferred_to || undefined,
        });
      }
      setDialog(null);
    } catch (e) {
      const d = (e as { detail?: { message?: string } }).detail;
      setErr(d?.message ?? (e as Error).message);
    }
  }

  const badge = STATUS_BADGE[status] ?? { label: status, cls: "bg-slate-100 text-slate-600" };

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 mb-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold text-slate-600 uppercase tracking-wide">
            {t("lifecycle.title")}
          </h2>
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge.cls}`}>{badge.label}</span>
        </div>
        {canManage && (
          <div className="flex gap-2">
            {actions.map(([to, rule]) => (
              <button
                key={to}
                onClick={() => open(to, rule)}
                className={`px-3 py-1 rounded border text-xs ${
                  to === "active"
                    ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    : "border-slate-300 text-slate-600 hover:bg-slate-50"
                }`}
              >
                {rule.label_zh ?? to}
              </button>
            ))}
            {actions.length === 0 && (
              <span className="text-xs text-slate-400">{t("lifecycle.terminal")}</span>
            )}
          </div>
        )}
      </div>

      {/* 时间线 */}
      {timeline.data && timeline.data.length > 0 && (
        <ol className="mt-4 border-l-2 border-slate-100 pl-4 space-y-2">
          {timeline.data.map((e, i) => (
            <li key={i} className="text-xs text-slate-600">
              <span className="text-slate-400 font-mono mr-2">{e.at.slice(0, 10)}</span>
              {e.kind === "transfer" ? (
                <>
                  {t("lifecycle.transferEvent")}:{e.from_school} → {e.to_school}
                  {e.reason && <span className="ml-2 text-slate-400">{e.reason}</span>}
                </>
              ) : e.kind === "interview" ? (
                <>
                  {t("lifecycle.interviewEvent")}
                  {e.overall_score != null && <span className="ml-1 text-slate-400">({e.overall_score})</span>}
                </>
              ) : (
                <>
                  {STATUS_BADGE[e.from_status ?? ""]?.label ?? e.from_status} → {" "}
                  <span className="font-medium">{STATUS_BADGE[e.to_status ?? ""]?.label ?? e.to_status}</span>
                  {e.reason && <span className="ml-2 text-slate-400">{e.reason}</span>}
                  {e.expected_return_date && (
                    <span className="ml-2 text-amber-600">{t("lifecycle.expectedReturn", { date: e.expected_return_date })}</span>
                  )}
                  {e.transferred_to && <span className="ml-2 text-slate-400">→ {e.transferred_to}</span>}
                </>
              )}
            </li>
          ))}
        </ol>
      )}

      {/* 状态变更弹窗:必含影响预览 */}
      {dialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setDialog(null)}>
          <div className="w-[28rem] rounded-xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-slate-800 mb-2">{dialog.rule.label_zh}</h3>
            {dialog.rule.effects_zh && (
              <p className="mb-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                {dialog.rule.effects_zh}
              </p>
            )}
            {(dialog.rule.required ?? []).concat(
              // reason 非必填的转换也给可选原因框(复学)
              (dialog.rule.required ?? []).includes("reason") ? [] : ["reason"],
            ).map((f) => (
              <label key={f} className="block mb-2 text-xs text-slate-600">
                {FIELD_LABEL[f] ?? f}
                {(dialog.rule.required ?? []).includes(f) && <span className="text-rose-500"> *</span>}
                {f.includes("date") ? (
                  <input
                    type="date"
                    value={fields[f] ?? ""}
                    onChange={(e) => setFields((m) => ({ ...m, [f]: e.target.value }))}
                    className="mt-1 w-full rounded border border-slate-200 px-2 py-1"
                  />
                ) : (
                  <input
                    type="text"
                    value={fields[f] ?? ""}
                    onChange={(e) => setFields((m) => ({ ...m, [f]: e.target.value }))}
                    className="mt-1 w-full rounded border border-slate-200 px-2 py-1"
                  />
                )}
              </label>
            ))}
            {err && <p className="text-xs text-rose-600 mb-2">{err}</p>}
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => setDialog(null)} className="px-3 py-1.5 rounded border border-slate-200 text-sm text-slate-600">
                {t("common.cancel")}
              </button>
              <button
                onClick={submit}
                disabled={change.isPending || reinstate.isPending}
                className="px-3 py-1.5 rounded bg-sky-600 text-white text-sm hover:bg-sky-700 disabled:opacity-50"
              >
                {t("lifecycle.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
